import { createContext, useCallback, useContext, useMemo, useReducer, useRef } from 'react';
import type { ReactNode } from 'react';
import { chatsApi, type ChatListItem } from '@/services/chats';

export type ChatsStatus = 'idle' | 'loading' | 'success' | 'error';

type AccountKey = string;

type AccountChatsState = {
  status: ChatsStatus;
  data: ChatListItem[];
  error: Error | null;
};

type ChatsState = Record<AccountKey, AccountChatsState>;

const initialAccountState: AccountChatsState = {
  status: 'idle',
  data: [],
  error: null,
};

const initialState: ChatsState = {};
const chatsKeyOf = (accountId: string | number, sheetId?: number) =>
  sheetId != null ? `${accountId}:sheet:${sheetId}` : String(accountId);

type ChatsAction =
  | { type: 'start'; accountKey: AccountKey }
  | { type: 'success'; accountKey: AccountKey; data: ChatListItem[] }
  | { type: 'error'; accountKey: AccountKey; error: Error }
  | { type: 'set'; accountKey: AccountKey; data: ChatListItem[] };

const reducer = (state: ChatsState, action: ChatsAction): ChatsState => {
  const base = state[action.accountKey] ?? initialAccountState;
  switch (action.type) {
    case 'start':
      return {
        ...state,
        [action.accountKey]: {
          ...base,
          status: 'loading',
          error: null,
        },
      };
    case 'success':
      return {
        ...state,
        [action.accountKey]: {
          status: 'success',
          data: action.data,
          error: null,
        },
      };
    case 'error':
      return {
        ...state,
        [action.accountKey]: {
          status: 'error',
          data: [],
          error: action.error,
        },
      };
    case 'set':
      return {
        ...state,
        [action.accountKey]: {
          status: 'success',
          data: action.data,
          error: null,
        },
      };
    default:
      return state;
  }
};

type ChatsContextValue = {
  state: ChatsState;
  load: (
    accountId: string | number,
    options?: { force?: boolean; sheetId?: number }
  ) => Promise<ChatListItem[]>;
  create: (accountId: string | number, title: string) => Promise<ChatListItem>;
  remove: (accountId: string | number, chatId: string | number) => Promise<boolean>;
  upsert: (accountId: string | number, chat: ChatListItem) => void;
};

const ChatsContext = createContext<ChatsContextValue | null>(null);

export const ChatsProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const pendingRef = useRef(new Map<AccountKey, Promise<ChatListItem[]>>());
  const stateRef = useRef(state);

  // Синхронизируем ref с state
  stateRef.current = state;

  const load = useCallback<ChatsContextValue['load']>(
    async (accountId, options) => {
      const accountKey = chatsKeyOf(accountId, options?.sheetId);
      const shouldForce = options?.force ?? false;

      if (!shouldForce) {
        const pending = pendingRef.current.get(accountKey);
        if (pending) return pending;

        // Используем stateRef для получения актуального состояния без добавления в deps
        const existing = stateRef.current[accountKey];
        if (existing && (existing.status === 'success' || existing.status === 'loading')) {
          return existing.data;
        }
      }

      const request = (async () => {
        dispatch({ type: 'start', accountKey });
        try {
          const data = await chatsApi.list(accountId, { sheetId: options?.sheetId });
          dispatch({ type: 'success', accountKey, data });
          return data;
        } catch (error) {
          const err = error instanceof Error ? error : new Error('Failed to fetch chats');
          dispatch({ type: 'error', accountKey, error: err });
          throw err;
        } finally {
          pendingRef.current.delete(accountKey);
        }
      })();

      pendingRef.current.set(accountKey, request);
      return request;
    },
    []
  );

  const create = useCallback<ChatsContextValue['create']>(
    async (accountId, title) => {
      const accountKey = String(accountId);
      const chat = await chatsApi.create(accountId, { title });
      const current = stateRef.current[accountKey]?.data ?? [];
      const next = [chat, ...current];
      dispatch({ type: 'set', accountKey, data: next });
      return chat;
    },
    []
  );

  const remove = useCallback<ChatsContextValue['remove']>(
    async (accountId, chatId) => {
      const accountKey = String(accountId);
      await chatsApi.delete(accountId, chatId);
      const current = stateRef.current[accountKey]?.data ?? [];
      const next = current.filter(item => String(item.id) !== String(chatId));
      dispatch({ type: 'set', accountKey, data: next });
      return true;
    },
    []
  );

  const upsert = useCallback<ChatsContextValue['upsert']>((accountId, chat) => {
    const accountKey = String(accountId);
    const current = stateRef.current[accountKey]?.data ?? [];
    const index = current.findIndex(item => item.id === chat.id);
    const next =
      index >= 0
        ? [...current.slice(0, index), chat, ...current.slice(index + 1)]
        : [chat, ...current];
    dispatch({ type: 'set', accountKey, data: next });
  }, []);

  const value = useMemo<ChatsContextValue>(
    () => ({
      state,
      load,
      create,
      remove,
      upsert,
    }),
    [state, load, create, remove, upsert]
  );

  return <ChatsContext.Provider value={value}>{children}</ChatsContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useChatsContext = () => {
  const ctx = useContext(ChatsContext);
  if (!ctx) {
    throw new Error('useChatsContext must be used within a ChatsProvider');
  }
  return ctx;
};
